import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import PacotesBox from '@/components/feature/PacotesBox';

const beneficios = [
  {
    icon: 'ri-headphone-line',
    titulo: 'Imersão sem ruído externo',
    desc: 'Cada participante ouve a experiência direto no fone, isolado do barulho do evento, da feira ou do shopping. A mensagem da marca chega inteira — e sem incomodar quem está em volta.',
  },
  {
    icon: 'ri-palette-line',
    titulo: 'LED na cor da marca',
    desc: 'O modelo LED UHF tem anel de luz com cor personalizável: dá para acender todos os fones na cor da identidade visual da marca e transformar o público em parte da cenografia.',
  },
  {
    icon: 'ri-camera-lens-line',
    titulo: 'Experiência que vira conteúdo',
    desc: 'Um grupo de pessoas com fones iluminados chama a atenção de quem passa e rende fotos e vídeos espontâneos nas redes sociais — a ativação continua sendo vista depois que o evento acaba.',
  },
];

const usos = [
  { icon: 'ri-goblet-line', titulo: 'Degustação guiada', desc: 'Um especialista conduz a degustação no fone de cada convidado, sem precisar gritar no meio do evento.' },
  { icon: 'ri-rocket-line', titulo: 'Lançamento de produto', desc: 'Apresentação do produto com áudio exclusivo para o público convidado, mesmo em espaços abertos ou barulhentos.' },
  { icon: 'ri-route-line', titulo: 'Tour imersivo', desc: 'Percurso guiado por um espaço de marca, uma fábrica ou uma exposição, com narração no fone e grupo em movimento.' },
  { icon: 'ri-music-2-line', titulo: 'Silent disco de marca', desc: 'Pista com vários canais de música e fones na cor da marca, para festas corporativas, convenções e festivais.' },
  { icon: 'ri-store-2-line', titulo: 'Espaço de marca em festival ou feira', desc: 'Uma bolha de experiência dentro do evento: quem coloca o fone entra no universo da marca, sem disputar som com o palco.' },
  { icon: 'ri-mic-line', titulo: 'Talk e workshop no estande', desc: 'Conteúdo ao vivo para um grupo pequeno no meio do pavilhão, com áudio limpo para quem está ouvindo.' },
];

const etapas = [
  {
    num: '01',
    titulo: 'Briefing da ativação',
    desc: 'Entendemos o conceito, o espaço, o público esperado e o tempo de cada sessão para dimensionar fones, canais e transmissores.',
  },
  {
    num: '02',
    titulo: 'Desenho dos canais de áudio',
    desc: 'Definimos o que toca em cada canal — narração, música, palestra ou DJ — e a cor de LED de cada um. São até 10 canais simultâneos.',
  },
  {
    num: '03',
    titulo: 'Instalação e teste no local',
    desc: 'Transmissores discretos montados antes da abertura, com teste de alcance em todo o espaço da ativação.',
  },
  {
    num: '04',
    titulo: 'Operação durante o evento',
    desc: 'Técnico STIB no local cuidando da entrega e recolhimento dos fones, das baterias e da troca de canais.',
  },
  {
    num: '05',
    titulo: 'Desmontagem e higienização',
    desc: 'Recolhimento ao final e higienização de todos os fones, sem logística extra para a sua equipe.',
  },
];

const venuesFeiraSP = [
  { nome: 'Transamerica Expo Center', bairro: 'Santo Amaro', slug: 'palestra-silenciosa-transamerica-expo' },
  { nome: 'Expo Center Norte', bairro: 'Vila Guilherme', slug: 'palestra-silenciosa-expocenter-norte' },
  { nome: 'Distrito Anhembi', bairro: 'Santana', slug: 'palestra-silenciosa-distrito-anhembi' },
  { nome: 'Centro de Convenções Frei Caneca', bairro: 'Consolação', slug: 'palestra-silenciosa-frei-caneca' },
  { nome: 'WTC Events Center', bairro: 'Brooklin', slug: 'palestra-silenciosa-wtc-events-center' },
  { nome: 'Villa Bisutti', bairro: 'Vila Olímpia', slug: 'palestra-silenciosa-villa-bisutti' },
  { nome: 'Tokio Marine Hall', bairro: 'Várzea de Baixo', slug: 'palestra-silenciosa-tokio-marine-hall' },
];

const modelosFone = [
  {
    img: '/img/stib/fone-led-uhf.webp',
    alt: 'Fone de ouvido sem fio STIB com anel de LED colorido personalizável, modelo UHF',
    badge: 'UHF · LED personalizável',
    titulo: 'Fone Sem Fio LED UHF',
    desc: 'Anel de LED com cor personalizável — pode ser ajustado para combinar com a identidade visual da marca. Transmissão UHF de longo alcance, ideal para eventos com muitos sinais de rádio ao mesmo tempo. Bateria de 10 a 12 horas de uso contínuo.',
  },
  {
    img: '/img/stib/fone-vhf.jpg',
    alt: 'Fone de ouvido sem fio STIB com visor de canal integrado, modelo VHF',
    badge: 'VHF · Visor de canal',
    titulo: 'Fone Sem Fio VHF',
    desc: 'Modelo compacto com visor de canal integrado, para o visitante confirmar de relance em qual transmissão está conectado. Transmissão VHF estável, a opção mais usada da STIB para uso intensivo durante os dias de feira. Bateria de 10 a 12 horas de uso contínuo.',
  },
];

const perguntas = [
  {
    q: 'O que é uma ativação de experiência com fones sem fio?',
    a: 'É uma ação de marca em que o público usa fones sem fio para viver uma experiência de áudio exclusiva — degustação guiada, lançamento, tour imersivo ou silent disco — isolada do barulho do evento e, com o fone LED, na cor da marca.',
  },
  {
    q: 'Dá para deixar os fones na cor da marca?',
    a: 'Sim. O modelo LED UHF tem anel de luz com cor personalizável, que pode ser ajustada à identidade visual da marca. Quando há mais de um canal, cada canal também pode ter sua própria cor.',
  },
  {
    q: 'Quantos canais de áudio posso usar ao mesmo tempo?',
    a: 'Até 10 canais simultâneos. Isso permite, por exemplo, uma narração em português e outra em inglês, ou vários DJs em uma silent disco de marca.',
  },
  {
    q: 'Quem produz o conteúdo de áudio da ativação?',
    a: 'O conteúdo — música, narração, roteiro ou apresentação ao vivo — normalmente vem da marca ou da agência. A STIB cuida da parte técnica: fones, transmissores, distribuição dos canais e operação no local.',
  },
  {
    q: 'A STIB já operou fones LED em eventos grandes?',
    a: 'Sim. Em 2026, a STIB operou 500 fones LED UHF no MIND 360, no WTC Events Center, em três idiomas. Em 2025, operou 2.000 fones sem fio no Encontro Mundial da Deficiência Visual, no Distrito Anhembi.',
  },
  {
    q: 'Com quanta antecedência devo reservar?',
    a: 'Recomendamos ao menos 15 dias para ativações de até 100 fones e 30 dias para ações maiores ou com vários dias. Demandas urgentes são atendidas conforme disponibilidade.',
  },
];

function BenefitCard({ icon, titulo, desc }: { icon: string; titulo: string; desc: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-100 dark:border-gray-700 hover:border-primary/30 hover:shadow-lg transition-all duration-300">
      <div className="w-14 h-14 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center mb-6">
        <i className={`${icon} text-2xl text-primary`} aria-hidden="true"></i>
      </div>
      <h3 className="font-heading font-bold text-xl text-secondary dark:text-white mb-3">{titulo}</h3>
      <p className="font-body text-gray-600 dark:text-gray-300 leading-relaxed">{desc}</p>
    </div>
  );
}

function FaqItem({ question, answer, index, isVisible }: { question: string; answer: string; index: number; isVisible: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`border-b border-gray-200 dark:border-gray-700 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${index * 70}ms` }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full py-7 px-6 flex items-center justify-between text-left cursor-pointer group hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <h3 className="font-heading font-semibold text-lg text-secondary dark:text-white pr-8 group-hover:text-primary transition-colors">
          {question}
        </h3>
        <i className={`${open ? 'ri-subtract-line' : 'ri-add-line'} text-2xl text-primary flex-shrink-0 transition-all duration-300`}></i>
      </button>
      <div className={`grid transition-all duration-300 ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <p className="font-body text-gray-600 dark:text-gray-300 leading-relaxed px-6 pb-7">{answer}</p>
        </div>
      </div>
    </div>
  );
}

export default function AtivacaoExperienciaPage() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: beneficiosRef, isVisible: beneficiosVisible } = useScrollAnimation({ threshold: 0.1 });
const { ref: modelosRef, isVisible: modelosVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: usosRef, isVisible: usosVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: etapasRef, isVisible: etapasVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation({ threshold: 0.05 });

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

    document.title = 'Ativação de Experiência com Fones Sem Fio em São Paulo | STIB';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Ativação de marca com fones sem fio em São Paulo: degustação guiada, lançamentos, tours imersivos e silent disco com fones LED na cor da marca. Até 10 canais. STIB (11) 91777-6227.'
      );
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute(
        'content',
        'ativação de marca com fones, ativação de experiência, silent experience, fones LED personalizados, silent disco de marca, ativação em evento São Paulo'
      );
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${siteUrl}/ativacao-de-experiencia-com-fones-sao-paulo`);

    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Service',
          '@id': `${siteUrl}/ativacao-de-experiencia-com-fones-sao-paulo#service`,
          name: 'Ativação de Experiência com Fones Sem Fio',
          alternateName: ['Ativação de Marca com Fones', 'Silent Experience', 'Silent Disco de Marca'],
          description:
            'Ativação de marca com fones de ouvido sem fio em São Paulo: experiências imersivas com áudio exclusivo, até 10 canais simultâneos e fones LED com cor personalizável. Inclui transmissores, montagem e operação técnica no local.',
          provider: {
            '@type': 'LocalBusiness',
            name: 'STIB — Palestra Silenciosa Brasil',
            telephone: '+55-11-91777-6227',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'São Paulo',
              addressRegion: 'SP',
              addressCountry: 'BR',
            },
          },
          serviceType: 'Ativação de marca com fones de ouvido sem fio',
          areaServed: [
            { '@type': 'City', name: 'São Paulo' },
            { '@type': 'State', name: 'São Paulo' },
          ],
          offers: {
            '@type': 'Offer',
            name: 'Ativação com Fones — Orçamento Personalizado',
            description: 'Orçamento personalizado conforme o número de fones, de canais e de dias da ativação. Sem preço fixo publicado.',
            availability: 'https://schema.org/InStock',
            areaServed: 'BR',
            priceCurrency: 'BRL',
            url: `${siteUrl}/ativacao-de-experiencia-com-fones-sao-paulo`,
            seller: {
              '@type': 'LocalBusiness',
              name: 'STIB — Palestra Silenciosa Brasil',
              telephone: '+55-11-91777-6227',
            },
          },
        },
        {
          '@type': 'FAQPage',
          mainEntity: perguntas.map((p) => ({
            '@type': 'Question',
            name: p.q,
            acceptedAnswer: { '@type': 'Answer', text: p.a },
          })),
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Serviços', item: `${siteUrl}/servicos` },
            { '@type': 'ListItem', position: 3, name: 'Ativação de Experiência com Fones', item: `${siteUrl}/ativacao-de-experiencia-com-fones-sao-paulo` },
          ],
        },
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/ativacao-de-experiencia-com-fones-sao-paulo#webpage`,
          url: `${siteUrl}/ativacao-de-experiencia-com-fones-sao-paulo`,
          name: 'Ativação de Experiência com Fones Sem Fio em São Paulo | STIB',
          description: 'Ativação de marca com fones sem fio em São Paulo: experiências imersivas com áudio exclusivo e fones LED na cor da marca.',
          inLanguage: 'pt-BR',
          isPartOf: { '@id': `${siteUrl}/#website` },
          about: { '@id': `${siteUrl}/ativacao-de-experiencia-com-fones-sao-paulo#service` },
          dateModified: new Date().toISOString().split('T')[0],
        },
      ],
    };

    const existingSchema = document.getElementById('ativacao-experiencia-schema');
    if (existingSchema) existingSchema.remove();
    const script = document.createElement('script');
    script.id = 'ativacao-experiencia-schema';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('ativacao-experiencia-schema');
      if (s) s.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden pt-20">
          <div className="absolute inset-0">
            <img
              src="/img/stib/festa-silenciosa-confraternizacao-fones-led.webp"
              alt="Ativação de experiência com fones sem fio em São Paulo - STIB"
              className="w-full h-full object-cover object-top"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-secondary/85 via-secondary/65 to-secondary/40"></div>
          </div>
          <div
            ref={heroRef}
            className={`relative max-w-7xl mx-auto px-6 w-full transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <nav className="flex items-center gap-2 text-sm mb-8" aria-label="Breadcrumb">
              <Link to="/" className="text-white/70 hover:text-white transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line text-white/50" aria-hidden="true"></i>
              <Link to="/servicos" className="text-white/70 hover:text-white transition-colors">Serviços</Link>
              <i className="ri-arrow-right-s-line text-white/50" aria-hidden="true"></i>
              <span className="text-white font-medium">Ativação de Experiência</span>
            </nav>
            <div className="max-w-3xl">
              <span className="inline-block bg-primary/20 text-primary border border-primary/30 text-sm font-semibold px-4 py-1.5 rounded-full mb-6 uppercase tracking-wider">
                Ativação de marca em São Paulo
              </span>
              <h1 className="font-heading font-extrabold text-5xl md:text-7xl text-white mb-6 leading-tight">
                ATIVAÇÃO DE EXPERIÊNCIA<br />
                COM <span className="text-primary">FONES SEM FIO</span>
              </h1>
              <p className="font-body text-xl text-white/85 leading-relaxed mb-10 max-w-2xl">
                Experiências de marca com áudio exclusivo no fone de cada participante: degustações guiadas,
                lançamentos, tours imersivos e silent disco, com fones LED na cor da marca e até 10 canais
                simultâneos — montagem e operação técnica incluídas.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contato"
                  className="inline-flex items-center justify-center gap-3 bg-primary text-black px-10 py-4 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-message-3-line text-lg" aria-hidden="true"></i>
                  Solicitar Orçamento Grátis
                </Link>
                <a
                  href="https://wa.me/5511917776227?text=Olá! Preciso de orçamento para uma ativação de experiência com fones sem fio."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-green-500 text-white px-10 py-4 rounded-full font-body font-bold text-base uppercase hover:bg-green-600 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-lg" aria-hidden="true"></i>
                  (11) 91777-6227
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-secondary dark:bg-gray-800 py-8">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { num: '+5.000', label: 'Fones disponíveis' },
                { num: '+500', label: 'Eventos realizados' },
                { num: 'Até 10', label: 'Canais simultâneos' },
                { num: '10–12h', label: 'Bateria por fone' },
              ].map((s, i) => (
                <div key={i}>
                  <div className="font-heading font-extrabold text-3xl md:text-4xl text-primary mb-1">{s.num}</div>
                  <div className="font-body text-sm text-white/70">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Por que fones sem fio no estande */}
        <section className="py-24 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-secondary dark:text-white mb-4">
                POR QUE USAR FONES NA SUA ATIVAÇÃO
              </h2>
              <p className="font-body text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                Em eventos cheios, a atenção do público é disputada por todos os lados.
                O fone cria um espaço só da marca, sem aumentar o barulho em volta.
              </p>
            </div>
            <div ref={beneficiosRef} className="grid md:grid-cols-3 gap-8">
              {beneficios.map((b, i) => (
                <div
                  key={i}
                  className={`transition-all duration-500 ${beneficiosVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <BenefitCard icon={b.icon} titulo={b.titulo} desc={b.desc} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Modelos de fone disponiveis */}
<section className="py-24 bg-white dark:bg-gray-900">
  <div className="max-w-6xl mx-auto px-6">
    <div className="text-center mb-16">
      <h2 className="font-heading font-bold text-4xl md:text-5xl text-secondary dark:text-white mb-4">
        MODELOS DE FONE DISPONÍVEIS
      </h2>
      <p className="font-body text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
        Dois modelos de fone sem fio da STIB — o briefing define qual faz mais sentido para a sua ativação.
      </p>
    </div>
    <div ref={modelosRef} className="grid sm:grid-cols-2 gap-8">
      {modelosFone.map((m, i) => (
        <div
          key={i}
          className={`bg-gray-50 dark:bg-gray-800 rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-700 hover:border-primary/30 hover:shadow-lg transition-all duration-500 ${modelosVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: `${i * 100}ms` }}
        >
          <div className="aspect-[4/3] overflow-hidden bg-gray-100 dark:bg-gray-900">
            <img src={m.img} alt={m.alt} className={`w-full h-full ${m.img.includes('vhf') ? 'object-contain object-center bg-white p-4' : 'object-cover'}`} loading="lazy" />
          </div>
          <div className="p-8">
            <span className="inline-block bg-primary/10 dark:bg-primary/20 text-primary text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-wider">{m.badge}</span>
            <h3 className="font-heading font-bold text-2xl text-secondary dark:text-white mb-3">{m.titulo}</h3>
            <p className="font-body text-gray-600 dark:text-gray-300 leading-relaxed">{m.desc}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
</section>

{/* Como funciona */}
        <section className="py-24 bg-white dark:bg-gray-900">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-secondary dark:text-white mb-4">
                COMO FUNCIONA A ATIVAÇÃO
              </h2>
              <p className="font-body text-gray-600 dark:text-gray-300">Do briefing à desmontagem, com a parte técnica nas mãos da STIB</p>
            </div>
            <div ref={etapasRef} className="space-y-6">
              {etapas.map((e, i) => (
                <div
                  key={i}
                  className={`flex gap-6 items-start bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 md:p-8 transition-all duration-500 ${etapasVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                  style={{ transitionDelay: `${i * 90}ms` }}
                >
                  <span className="font-heading font-extrabold text-3xl text-primary/40 flex-shrink-0">{e.num}</span>
                  <div>
                    <h3 className="font-heading font-bold text-xl text-secondary dark:text-white mb-2">{e.titulo}</h3>
                    <p className="font-body text-gray-600 dark:text-gray-300 leading-relaxed">{e.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Usos mais comuns */}
        <section className="py-24 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-secondary dark:text-white mb-4">
                FORMATOS DE ATIVAÇÃO COM FONES
              </h2>
              <p className="font-body text-gray-600 dark:text-gray-300">Um mesmo kit de fones, várias experiências de marca</p>
            </div>
            <div ref={usosRef} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {usos.map((u, i) => (
                <div
                  key={i}
                  className={`bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 transition-all duration-500 ${usosVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 70}ms` }}
                >
                  <i className={`${u.icon} text-3xl text-primary mb-4 block`} aria-hidden="true"></i>
                  <h3 className="font-heading font-bold text-lg text-secondary dark:text-white mb-2">{u.titulo}</h3>
                  <p className="font-body text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{u.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Venues de feira em SP */}
        <section className="py-24 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-4xl text-secondary dark:text-white mb-3">
                CENTROS DE EVENTOS EM SÃO PAULO
              </h2>
              <p className="font-body text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                A STIB monta ativações nos principais espaços de eventos da cidade.
                Clique no seu venue para ver os detalhes de cada espaço.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {venuesFeiraSP.map((v, i) => (
                <Link
                  key={i}
                  to={`/locais/${v.slug}`}
                  className="group inline-flex items-center gap-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl px-6 py-4 hover:border-primary hover:bg-white dark:hover:bg-gray-700 transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-map-pin-2-line text-xl text-primary" aria-hidden="true"></i>
                  <span>
                    <span className="block font-heading font-bold text-secondary dark:text-white group-hover:text-primary transition-colors">{v.nome}</span>
                    <span className="block font-body text-xs text-gray-500 dark:text-gray-400">{v.bairro}</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-24 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-heading font-bold text-5xl text-secondary dark:text-white mb-4">
                PERGUNTAS FREQUENTES
              </h2>
              <p className="font-body text-gray-600 dark:text-gray-300">Sobre ativação de experiência com fones sem fio</p>
            </div>
            <div ref={faqRef} className="space-y-0 bg-white dark:bg-gray-900 rounded-2xl">
              {perguntas.map((p, i) => (
                <FaqItem key={i} question={p.q} answer={p.a} index={i} isVisible={faqVisible} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-24 bg-secondary dark:bg-gray-800">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="font-heading font-bold text-5xl md:text-6xl text-white mb-6">
              Vamos Criar a Sua Próxima Ativação?
            </h2>
            <p className="font-body text-xl text-white/85 mb-10 max-w-2xl mx-auto leading-relaxed">
              Conte o conceito, o local, a data e o público esperado. Respondemos em até 24 horas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contato"
                className="inline-flex items-center justify-center gap-3 bg-primary text-black px-10 py-5 rounded-full font-body font-bold text-lg uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-calendar-check-line text-xl" aria-hidden="true"></i>
                Solicitar Orçamento Grátis
              </Link>
              <a
                href="tel:+5511917776227"
                className="inline-flex items-center justify-center gap-3 border-2 border-white text-white px-10 py-5 rounded-full font-body font-bold text-lg uppercase hover:bg-white hover:text-secondary transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl" aria-hidden="true"></i>
                (11) 91777-6227
              </a>
            </div>
          </div>
        </section>
      <PacotesBox texto="Marcas e agências com várias ativações no ano podem fechar um pacote para o calendário inteiro, com valor previsível e economia em relação a contratar ação por ação." />
      </main>
      <Footer />
    </div>
  );
}
